<script setup lang="ts">
import { ref, Ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "../../../store/index";
import axios from "axios";
import { suggestedDomainsList, Domains } from "../../../utils/constants";
import { addRetargetingScripts } from "../../../utils/retargeting";
import { pushEmail } from "../../../utils/pu";
const { t } = useI18n();
const store = useStore();

const email = ref("");
const emailError = ref();
const isLoading = ref(false);
const lpDomain = ref(window.location.host + window.location.pathname);

const state = ref(store.state).value;
const istate =
	ref(store.urlParameters?.flow).value === "withemailstate" && state
		? true
		: false;
const showSkipButton = ref(false);

const trackingDomain = store.trackingDomain;

const re =
	/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const country = ref(
	store.urlParameters?.lang?.slice(0, 2) ?? "hu",
) as unknown as Ref<Domains>;
const showSuggestion = ref(false);

const suggestedResults = () =>
	suggestedDomainsList[country.value].map((domain: string) => {
		return email.value.split("@")[0] + domain;
	});

const watcher = watchEffect(() => {
	if (email.value.includes("@")) {
		suggestedResults();
		showSuggestion.value = true;
	}
	if (re.test(email.value)) {
		showSuggestion.value = false;
	}
});

function selectDomain(address: string) {
	email.value = address;
	showSuggestion.value = false;
}

function skipButton() {
	window.location.href = `https://${trackingDomain}/click`;
}

function startCounter() {
	setTimeout(function () {
		showSkipButton.value = true;
	}, 4000);
}

function forwarder() {
	if (store.urlParameters?.forward === "nva") {
		window.location.href = `https://${trackingDomain}/click?sub5=${email.value}`;
		return true;
	}
	if (store.urlParameters?.forward === "to") {
		const emailBase64 = ref(btoa(`{"email": "${email.value}"}`));
		window.location.href = `https://${trackingDomain}/click?_fData=${emailBase64.value}`;
		return true;
	}
	if (store.urlParameters?.forward === "tm") {
		window.location.href = `https://${trackingDomain}/click?email=${email.value}`;
		return true;
	}
	if (store.urlParameters?.forward === "moar") {
		window.location.href = `https://${trackingDomain}/click?email=${email.value}`;
		return true;
	}
	if (store.urlParameters?.forward === "nex") {
		window.location.href = `https://${trackingDomain}/click?email=${email.value}`;
		return true;
	} else {
		window.location.href = `https://${trackingDomain}/click`;
		return true;
	}
}

async function validateEmail() {
	isLoading.value = true;
	if (email.value === undefined) {
		emailError.value = t("withEmail.errorNoEmail");
		isLoading.value = false;
		startCounter();
	} else if (!re.test(email.value)) {
		emailError.value = t("withEmail.errorInvalidEmail");
		isLoading.value = false;
		startCounter();
	} else {
		emailError.value = undefined;

		await pushEmail(
			email.value,
			store.urlParameters,
			window.location.host + window.location.pathname,
		);
		forwarder();
	}
}

function checkInputFocusCondition() {
	addRetargetingScripts();
}

const orientation = ref(store.urlParameters?.orientation.split("-")[0]);
</script>

<template>
	<div
		class="flex flex-col items-center w-full max-w-xl mx-auto space-y-5 text-center mt-7"
	>
		<!-- <h1 class="text-[#626c72] text-2xl font-normal tracking-[0.2rem] md:font-semibold uppercase">
            {{ t(`withEmail.${orientation}.text1`) }}
        </h1> -->
		<h1
			class="text-[#626c72] text-2xl font-normal tracking-[0.2rem] md:font-semibold uppercase"
			v-html="
				istate
					? t(`withEmail.${orientation}.text1state`, { state: state })
					: t(`withEmail.${orientation}.text1`)
			"
		></h1>
		<h2
			class="text-[#677277] tracking-[0.2rem] uppercase"
			v-html="
				istate
					? t(`withEmail.${orientation}.text2state`, { state: state })
					: t(`withEmail.${orientation}.text2`)
			"
		></h2>
		<h2 class="text-[#677277] tracking-[0.2rem] uppercase">
			{{ t(`withEmail.${orientation}.text3`) }}
		</h2>
		<div>
			<input
				type="email"
				name=""
				id=""
				class="rounded-sm"
				:placeholder="t('withEmail.inputPlaceholder')"
				autocomplete="off"
				v-model="email"
				@focus="checkInputFocusCondition()"
			/>
			<div class="relative z-30 max-w-xl">
				<ul
					v-if="showSuggestion && store.urlParameters?.ehelp"
					class="absolute w-full overflow-y-scroll bg-white border border-gray-300 rounded h-36"
				>
					<li
						v-for="domain in suggestedResults()"
						:key="domain"
						class="p-1 cursor-pointer hover:bg-gray-400"
						@click="selectDomain(domain)"
					>
						{{ domain }}
					</li>
				</ul>
			</div>
			<div class="h-6 mt-2">
				<h1
					v-if="emailError"
					class="font-semibold text-center text-red-600"
				>
					{{ emailError }}
				</h1>
			</div>
		</div>
		<div
			class="flex flex-col items-center justify-center w-full max-w-sm space-y-3 sm:flex-row sm:space-x-4 sm:space-y-0"
		>
			<button
				:disabled="isLoading"
				class="border border-[#cbcfd2] py-2 px-6 rounded-md text-sm hover:border-red-400 hover:text-red-400 duration-200 uppercase font-bold w-1/2"
				@click="validateEmail()"
			>
				{{ t(`withEmail.${orientation}.submitButton`) }}
			</button>
			<button
				:disabled="isLoading"
				v-if="showSkipButton"
				class="border border-[#cbcfd2] py-2 px-6 rounded-md text-sm hover:border-red-400 hover:text-red-400 duration-200 uppercase font-bold w-1/2 fadeInUp"
				@click="skipButton()"
			>
				{{ t(`withEmail.${orientation}.skipButton`) }}
			</button>
		</div>
	</div>
</template>

<style>
.fadeInUp {
	-webkit-animation: b 0.6s forwards;
	animation: b 0.6s forwards;
}

@-webkit-keyframes b {
	0% {
		opacity: 0;
		transform: translate3d(0, 10rem, 0);
	}

	to {
		opacity: 1;
		transform: translateZ(0);
	}
}

@keyframes b {
	0% {
		opacity: 0;
		transform: translate3d(0, 10rem, 0);
	}

	to {
		opacity: 1;
		transform: translateZ(0);
	}
}
</style>
